import { formatIsoToDate, money } from '../../../utils';

// PRST-134 — shaping for the worker cover block.
//
// This module is deliberately pure: no React, no @react-pdf/renderer. The results screen
// (WorkerSummary, DOM) and page 1 of the PDF (react-pdf primitives) cannot share markup —
// react-pdf's reconciler rejects DOM host elements — so they share this instead. Both map the
// same ordered array of plain { label, value } strings, which is what "the PDF shows the cover
// data in the same format" means in the ticket, and it removes the whole class of bug where the
// screen and the document quietly disagree.
//
// Redacted 2026-09-23 at the business's request (JoRay Rascon via John Bozeman, after stage UAT):
// Worker SSN, Worker Date of Birth and Health Care Coverage are no longer on the packet, so the
// recipients need not redact it by hand. The proc (sp_cl_cp_bi_pv) stopped returning those columns
// in the same change; this module never reads them either, so a stale proc cannot leak them back
// through the screen or the PDF. The SSN remains a search criterion — only the output changed.
//
// PRST-141 (2026-10-05, business via Daniel Prath): PRST drops Termination Status and Termination
// Date from the packet; Centerline keeps them. The brand is a build-time constant
// (REACT_APP_BRAND, the same switch getEdgeReportingMenu uses for report 22), so the filter lives
// here — the one place both the screen and the PDF read — and the proc keeps emitting the columns
// for Centerline. Last Day Worked is not a termination field and stays on both brands.

export const COVER_LABELS = [
  'Worker ID Number',
  'Worker Name',
  'Worker Latest Mailing Address',
  'Worker Latest Home Address',
  'Date Hired',
  'Last Day Worked',
  'Job Title',
  'Worker Primary Phone Number',
  'Last Pay Rate',
  'Termination Status',
  'Termination Date',
];

// Columns the proc used to return and this module must never read. Exported so the test can pin
// the list; there is no lint rule for "do not render this field".
export const REDACTED_COLUMNS = ['SSNLast4', 'DateOfBirth', 'HealthCareCoverage'];

// Labels PRST leaves off the cover block (PRST-141). Exported so the test can pin the list.
export const PRST_HIDDEN_LABELS = ['Termination Status', 'Termination Date'];

export const coverLabelsFor = (brand = process.env.REACT_APP_BRAND) =>
  brand === 'PRST' ? COVER_LABELS.filter(label => !PRST_HIDDEN_LABELS.includes(label)) : COVER_LABELS;

// Address parts arrive as separate columns; blanks collapse rather than leaving "City,  12345".
export const formatAddress = ({ line1, line2, city, state, postCode } = {}) => {
  const street = [line1, line2].map(part => (part || '').trim()).filter(Boolean).join(', ');
  const cityState = [(city || '').trim(), (state || '').trim()].filter(Boolean).join(', ');
  const locality = [cityState, (postCode || '').trim()].filter(Boolean).join(' ');
  return [street, locality].filter(Boolean).join(', ');
};

// Ticket: "Show N/A if no termination, otherwise show code and description (i.e., E - Term
// Eligible for Rehire)." A code with no description still prints the code rather than "E - ".
export const formatTermination = ({ code, description } = {}) => {
  const trimmedCode = (code || '').trim();
  const trimmedDescription = (description || '').trim();
  if (!trimmedCode && !trimmedDescription) return 'N/A';
  return [trimmedCode, trimmedDescription].filter(Boolean).join(' - ');
};

// `??`, not `||`: a pay rate or a worker id of 0 is falsy but is a real value, and `||` would
// blank it. This is the bug the AI Review document shipped with and had to fix (Tier 0).
const orBlank = value => ((value ?? '') === '' ? '' : value);

// Ticket: "Last Date Worked - Termination Date or if no Term Date - enter date of report."
// The report date is a render-time concept, not something the proc can know, so it is applied
// here — and it is the same value the PDF prints, because both go through buildCoverFields.
export const lastDateWorked = (cover = {}, reportDate = new Date()) =>
  formatIsoToDate(cover.LastDateWorked) || formatIsoToDate(cover.TerminationDate) || formatIsoToDate(reportDate);

const allCoverFields = (cover = {}, reportDate = new Date()) => [
  { label: 'Worker ID Number', value: String(orBlank(cover.WorkerId)) },
  { label: 'Worker Name', value: (cover.WorkerName || '').trim() },
  {
    label: 'Worker Latest Mailing Address',
    value: formatAddress({
      line1: cover.MailAddressLine1,
      line2: cover.MailAddressLine2,
      city: cover.MailCity,
      state: cover.MailState,
      postCode: cover.MailPostCode,
    }),
  },
  {
    label: 'Worker Latest Home Address',
    value: formatAddress({
      line1: cover.LiveAddressLine1,
      line2: cover.LiveAddressLine2,
      city: cover.LiveCity,
      state: cover.LiveState,
      postCode: cover.LivePostCode,
    }),
  },
  { label: 'Date Hired', value: formatIsoToDate(cover.DateHired) },
  { label: 'Last Day Worked', value: lastDateWorked(cover, reportDate) },
  { label: 'Job Title', value: (cover.JobTitle || '').trim() },
  { label: 'Worker Primary Phone Number', value: (cover.PrimaryPhone || '').trim() },
  {
    label: 'Last Pay Rate',
    value: (cover.LastPayRate ?? '') === '' ? '' : money.format(Number(cover.LastPayRate)),
  },
  {
    label: 'Termination Status',
    value: formatTermination({
      code: cover.TerminationStatusCode,
      description: cover.TerminationStatusDescription,
    }),
  },
  { label: 'Termination Date', value: formatIsoToDate(cover.TerminationDate) },
];

// The brand defaults from the env at CALL time (not module load) so a test can switch it per case.
export const buildCoverFields = (cover = {}, reportDate = new Date(), brand = process.env.REACT_APP_BRAND) => {
  const labels = coverLabelsFor(brand);
  return allCoverFields(cover, reportDate).filter(field => labels.includes(field.label));
};

// The mockup lays the cover block out as TWO columns, not one long column. The ticket lists the
// fields in column order, so the first half is the left column and the rest the right — both the
// screen and the PDF split it here rather than each deciding for itself. With an odd count (eleven
// since the redaction) the left column takes the extra row, so the block never reads bottom-heavy.
export const splitCoverColumns = (fields = []) => {
  const leftLength = Math.ceil(fields.length / 2);
  return { left: fields.slice(0, leftLength), right: fields.slice(leftLength) };
};
